import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Mail, CheckCircle2, XCircle, RefreshCw, AlertCircle, MapPin, Key, Copy, Plus, Building2, MessageSquare } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import EmailSettings from "../components/EmailSettings";
import EmailDiagnostics from "../components/EmailDiagnostics";
import { OutlookConnect } from "../components/outlook/OutlookConnect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Settings() {
  const [user, setUser] = useState(null);
  const [gingrConfig, setGingrConfig] = useState({
    api_key: "",
    app_url: "",
    gingr_location_id: null,
    gingr_location_name: null,
  });
  const [gingrLocations, setGingrLocations] = useState([]);
  const [fetchingGingrLocations, setFetchingGingrLocations] = useState(false);
  const [gingrLocationsError, setGingrLocationsError] = useState(null);
  const [savedGingrConfig, setSavedGingrConfig] = useState(null); // Stores the currently saved config
  const [gingrConnectionVerified, setGingrConnectionVerified] = useState(false);
  const [gmailConfig, setGmailConfig] = useState({
    email: "",
    app_password: "",
    subject_filter: "Booking Form Submission, Contact Us Form Submission",
  });
  const [gmailConnected, setGmailConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingGmail, setTestingGmail] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationForChange, setSelectedLocationForChange] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const [inviteCodes, setInviteCodes] = useState({});
  const [createLocationOpen, setCreateLocationOpen] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    timezone: 'America/New_York'
  });
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [knowledgeBaseStats, setKnowledgeBaseStats] = useState(null);
  const [aiFeedbackStats, setAiFeedbackStats] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchGmailStatus();
    fetchLocations();
    fetchPendingLocationRequest();
    fetchKnowledgeBaseStats();
    fetchAiFeedbackStats();
    fetchGingrStatus();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user");
    }
  };

  const fetchGmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/gmail/status`);
      setGmailStatus(response.data);
      setGmailConnected(response.data.configured);
    } catch (error) {
      console.error("Failed to fetch Gmail status");
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch locations");
    }
  };

  const fetchPendingLocationRequest = async () => {
    try {
      const response = await axios.get(`${API}/location-change-requests/my-request`);
      setPendingRequest(response.data.request);
    } catch (error) {
      console.error("Failed to fetch pending request");
    }
  };

  const fetchKnowledgeBaseStats = async () => {
    try {
      const response = await axios.get(`${API}/knowledge-base/stats`);
      setKnowledgeBaseStats(response.data);
    } catch (error) {
      console.error("Failed to fetch knowledge base stats");
    }
  };

  const fetchGingrStatus = async () => {
    try {
      const response = await axios.get(`${API}/gingr/status`);
      if (response.data.configured) {
        const config = response.data.config || {};
        setSavedGingrConfig({
          api_key: config.api_key || "",
          app_url: config.app_url || "",
          gingr_location_id: config.gingr_location_id || null,
          gingr_location_name: config.gingr_location_name || null,
        });
        setGingrConfig({
          api_key: config.api_key || "",
          app_url: config.app_url || "",
          gingr_location_id: config.gingr_location_id || null,
          gingr_location_name: config.gingr_location_name || null,
        });
        setGingrConnectionVerified(response.data.connection_verified || false);
      }
    } catch (error) {
      console.error("Failed to fetch Gingr status");
    }
  };

  const requestLocationChange = async () => {
    if (!selectedLocationForChange) {
      toast.error("Please select a location");
      return;
    }

    try {
      await axios.post(`${API}/location-change-requests?requested_location_id=${selectedLocationForChange}`);
      toast.success("Location change request submitted! Waiting for admin approval.");
      setSelectedLocationForChange("");
      fetchPendingLocationRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const generateInviteCode = async (locationId) => {
    try {
      const response = await axios.post(`${API}/locations/${locationId}/generate-invite-code`);
      toast.success(`Invite code generated: ${response.data.invite_code}`);
      setInviteCodes({...inviteCodes, [locationId]: response.data.invite_code});
      fetchLocations(); // Refresh to get updated invite codes
    } catch (error) {
      toast.error("Failed to generate invite code");
    }
  };

  const fetchAiFeedbackStats = async () => {
    try {
      const response = await axios.get(`${API}/ai/feedback/stats`);
      setAiFeedbackStats(response.data);
    } catch (error) {
      console.error("Failed to fetch AI feedback stats");
    }
  };

  const createLocation = async () => {
    if (!newLocation.name) {
      toast.error("Location name is required");
      return;
    }

    try {
      const response = await axios.post(`${API}/locations`, newLocation);
      toast.success(`Location "${newLocation.name}" created successfully!`);
      
      // Generate invite code for the new location
      const locationId = response.data.location_id;
      await generateInviteCode(locationId);
      
      // Reset form and close dialog
      setNewLocation({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        timezone: 'America/New_York'
      });
      setCreateLocationOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create location");
    }
  };

  const saveGingrConfig = async () => {
    try {
      await axios.post(`${API}/gingr/config`, gingrConfig);
      const locationInfo = gingrConfig.gingr_location_name ? ` (mapped to ${gingrConfig.gingr_location_name})` : '';
      toast.success(`Gingr POS config saved${locationInfo}!`);
      
      // Update saved config state to show the "connected" state
      setSavedGingrConfig({ ...gingrConfig });
      setGingrConnectionVerified(true);
      
      // Clear the locations dropdown since we've saved
      setGingrLocations([]);
    } catch (error) {
      toast.error("Failed to save Gingr config");
    }
  };

  const disconnectGingr = async () => {
    if (!window.confirm("Are you sure you want to disconnect this Gingr location? You'll need to reconfigure it.")) {
      return;
    }
    
    // Reset all Gingr states
    setGingrConfig({
      api_key: "",
      app_url: "",
      gingr_location_id: null,
      gingr_location_name: null,
    });
    setSavedGingrConfig(null);
    setGingrConnectionVerified(false);
    setGingrLocations([]);
    setGingrLocationsError(null);
    
    toast.info("Gingr configuration cleared. Enter new credentials to reconfigure.");
  };

  const fetchGingrLocations = async () => {
    if (!gingrConfig.api_key || !gingrConfig.app_url) {
      toast.error("Please enter API Key and App URL first");
      return;
    }
    
    setFetchingGingrLocations(true);
    setGingrLocationsError(null);
    
    try {
      const response = await axios.post(`${API}/gingr/fetch-locations`, null, {
        params: {
          api_key: gingrConfig.api_key,
          app_url: gingrConfig.app_url
        }
      });
      
      if (response.data.success) {
        setGingrLocations(response.data.locations || []);
        if (response.data.locations?.length === 0) {
          toast.info("Single-location account detected. No location mapping needed.");
        } else if (response.data.locations?.length === 1) {
          // Auto-select if only one location
          const loc = response.data.locations[0];
          setGingrConfig(prev => ({
            ...prev,
            gingr_location_id: loc.id,
            gingr_location_name: loc.name
          }));
          toast.success(`Found 1 location: ${loc.name}`);
        } else {
          toast.success(`Found ${response.data.locations.length} Gingr locations. Please select one.`);
        }
      } else {
        setGingrLocationsError(response.data.error || "Failed to fetch locations");
        toast.error(response.data.error || "Failed to fetch Gingr locations");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch Gingr locations";
      setGingrLocationsError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setFetchingGingrLocations(false);
    }
  };

  const handleGingrLocationSelect = (locationId) => {
    const location = gingrLocations.find(l => l.id === parseInt(locationId));
    if (location) {
      setGingrConfig(prev => ({
        ...prev,
        gingr_location_id: location.id,
        gingr_location_name: location.name
      }));
    }
  };

  const saveGmailConfig = async () => {
    try {
      await axios.post(`${API}/gmail/config`, gmailConfig);
      setGmailConnected(true);
      toast.success("Gmail IMAP config saved!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to save Gmail config";
      toast.error(errorMsg);
      if (error.response?.status === 403) {
        toast.error("Admin permissions required. Please login as admin.");
      }
    }
  };

  const testGmailConnection = async () => {
    setTestingGmail(true);
    try {
      const response = await axios.post(`${API}/gmail/test-connection`, gmailConfig);
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Connection test failed";
      toast.error(errorMsg);
    } finally {
      setTestingGmail(false);
    }
  };

  const syncGmailEmails = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/gmail/sync-emails`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync emails");
    } finally {
      setSyncing(false);
    }
  };

  const runLocationMigration = async () => {
    if (!window.confirm("This will assign Chalfont location to all leads that don't have a location. Continue?")) {
      return;
    }
    
    setMigrationRunning(true);
    setMigrationResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/run-location-migration`);
      setMigrationResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || "Migration failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to run migration";
      toast.error(errorMsg);
      setMigrationResult({ success: false, error: errorMsg });
    } finally {
      setMigrationRunning(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-sm sm:text-lg text-gray-600">Configure your integrations and preferences</p>
      </div>

      {/* Admin Permission Notice */}
      {user && !['admin', 'tenant_admin', 'super_admin'].includes(user.role) && (
        <Card className="card border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">Admin Permissions Required</h3>
                <p className="text-sm text-amber-800">
                  You are logged in as <span className="font-semibold">{user.email}</span> with <span className="font-semibold">{user.role}</span> role.
                  Only admins can configure integrations. Please login with an admin account or contact your administrator.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Admin account: <code className="bg-amber-100 px-2 py-1 rounded">admin@leadgentracker.com</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Notification Settings - For Admins */}
      {user && (['admin', 'tenant_admin'].includes(user.role) || user.role === 'super_admin') && (
        <EmailSettings user={user} locations={locations} />
      )}

      {/* Email Diagnostics - Super Admin only */}
      {user && user.role === 'super_admin' && (
        <EmailDiagnostics user={user} />
      )}

      {/* Microsoft 365 / Outlook */}
      <OutlookConnect user={user} />

      {/* Gmail IMAP Integration */}
      <Card className="card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>Gmail Email Forwarding (RECOMMENDED)</CardTitle>
              <CardDescription className="mt-1">Forward your contact form emails to a Gmail account for automatic lead capture</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gmail_email">Gmail Address</Label>
              <Input
                id="gmail_email"
                type="email"
                placeholder="leads@yourdomain.com"
                value={gmailConfig.email}
                onChange={(e) => setGmailConfig({ ...gmailConfig, email: e.target.value })}
                data-testid="gmail-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gmail_app_password">Gmail App Password</Label>
              <Input
                id="gmail_app_password"
                type="password"
                placeholder="Enter 16-character app password"
                value={gmailConfig.app_password}
                onChange={(e) => setGmailConfig({ ...gmailConfig, app_password: e.target.value })}
                data-testid="gmail-app-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject_filter">Email Subject Filter</Label>
              <Input
                id="subject_filter"
                placeholder="Contact Form on Chalfont"
                value={gmailConfig.subject_filter}
                onChange={(e) => setGmailConfig({ ...gmailConfig, subject_filter: e.target.value })}
                data-testid="subject-filter-input"
              />
              <p className="text-xs text-gray-500">Only process emails with this text in the subject line</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={testGmailConnection} 
              variant="outline" 
              disabled={testingGmail || !gmailConfig.email || !gmailConfig.app_password}
              data-testid="test-gmail-connection-button"
            >
              {testingGmail ? "Testing..." : "Test Connection"}
            </Button>
            <Button onClick={saveGmailConfig} variant="outline" data-testid="save-gmail-config-button">
              Save Configuration
            </Button>
            {gmailConnected && (
              <Button
                onClick={syncGmailEmails}
                disabled={syncing}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                data-testid="sync-gmail-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Syncing..." : "Sync Emails Now"}
              </Button>
            )}
          </div>

          <Separator />

          <div className="space-y-4 bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">Setup Instructions (5 minutes):</h4>
            
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-semibold">Step 1: Create Gmail Account</span>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Go to gmail.com and create a new account</li>
                  <li>Suggested: <code className="bg-white px-2 py-1 rounded text-xs">leads.chalfont@gmail.com</code></li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">Step 2: Enable 2-Step Verification</span>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Google Account Security</a></li>
                  <li>Turn on 2-Step Verification</li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">Step 3: Generate App Password</span>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">App Passwords</a></li>
                  <li>Create password for "Mail" on "Other (Custom name)"</li>
                  <li>Name it "LeadGen Tracker"</li>
                  <li>Copy the 16-character password</li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">Step 4: Set Up Outlook Auto-Forward Rule</span>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>In your work Outlook, go to Settings → Rules</li>
                  <li>Create new rule: "When subject contains 'Contact Form on Chalfont'"</li>
                  <li>Action: "Forward to" → <code className="bg-white px-2 py-1 rounded text-xs">leads.chalfont@gmail.com</code></li>
                  <li>Save rule</li>
                </ul>
              </div>

              <div>
                <span className="font-semibold">Step 5: Configure & Test</span>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Enter Gmail address and app password above</li>
                  <li>Click "Save Configuration"</li>
                  <li>Click "Sync Emails Now" to test</li>
                </ul>
              </div>
            </div>

            <div className="mt-4 p-3 bg-white border border-green-200 rounded">
              <p className="text-sm font-medium text-green-800">✅ Benefits of this approach:</p>
              <ul className="text-xs text-green-700 mt-2 space-y-1 list-disc list-inside">
                <li>No Azure AD admin access required</li>
                <li>Works with any email (Outlook, corporate, etc.)</li>
                <li>Simple 5-minute setup</li>
                <li>Automatically filters by subject line</li>
                <li>Instant lead capture</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-sync Status Indicator */}
      {gmailStatus && gmailStatus.configured && (
        <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-semibold text-green-900">Auto-Sync Active</span>
            </div>
          </div>
          <p className="text-sm text-green-800">
            ✅ Automatically checking for new emails every {gmailStatus.sync_interval}
          </p>
          {gmailStatus.last_sync && (
            <p className="text-xs text-green-600 mt-1">
              Last synced: {new Date(gmailStatus.last_sync).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Gingr POS */}
      <Card className="card">
        <CardHeader>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>Gingr POS Integration</CardTitle>
          <CardDescription className="mt-1">Connect to Gingr to match leads with existing customers and power the Going Home board</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Connected State - Show when saved config exists */}
          {savedGingrConfig && savedGingrConfig.gingr_location_name && (
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-green-900 text-lg">Connected</span>
                      {gingrConnectionVerified && (
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                          API Verified
                        </span>
                      )}
                    </div>
                    <p className="text-green-700 font-medium">
                      {savedGingrConfig.gingr_location_name}
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      Gingr Location ID: {savedGingrConfig.gingr_location_id} • {savedGingrConfig.app_url}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={disconnectGingr}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="disconnect-gingr-button"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Disconnect
                </Button>
              </div>
            </div>
          )}
          
          {/* Configuration Form - Show when NOT connected */}
          {!savedGingrConfig?.gingr_location_name && (
            <>
              <div className="space-y-2">
                <Label htmlFor="gingr_api_key">API Key</Label>
                <Input
                  id="gingr_api_key"
                  type="password"
                  placeholder="Enter Gingr API Key"
                  value={gingrConfig.api_key}
                  onChange={(e) => {
                    setGingrConfig({ ...gingrConfig, api_key: e.target.value });
                    setGingrLocations([]); // Reset locations when API key changes
                    setGingrLocationsError(null);
                  }}
                  data-testid="gingr-api-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gingr_app_url">App URL</Label>
                <Input
                  id="gingr_app_url"
                  placeholder="yourapp.gingrapp.com"
                  value={gingrConfig.app_url}
                  onChange={(e) => {
                    setGingrConfig({ ...gingrConfig, app_url: e.target.value });
                    setGingrLocations([]); // Reset locations when URL changes
                    setGingrLocationsError(null);
                  }}
                  data-testid="gingr-app-url-input"
                />
                <p className="text-xs text-gray-500">Do not include https:// - just your subdomain</p>
              </div>
              
              {/* Fetch Gingr Locations Button */}
              <div className="pt-2 border-t">
                <Button 
                  onClick={fetchGingrLocations} 
                  variant="outline"
                  disabled={fetchingGingrLocations || !gingrConfig.api_key || !gingrConfig.app_url}
                  className="w-full"
                  data-testid="fetch-gingr-locations-button"
                >
                  {fetchingGingrLocations ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Fetching Locations...
                    </>
                  ) : (
                    <>
                      <MapPin className="h-4 w-4 mr-2" />
                      Fetch Gingr Locations
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  Click to discover available locations in your Gingr account
                </p>
              </div>
              
              {/* Gingr Locations Dropdown */}
              {gingrLocations.length > 0 && (
                <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label htmlFor="gingr_location" className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-blue-900">
                      {gingrLocations.length} Gingr Location{gingrLocations.length > 1 ? 's' : ''} Found
                    </span>
                  </Label>
                  <Select
                    value={gingrConfig.gingr_location_id?.toString() || ""}
                    onValueChange={handleGingrLocationSelect}
                  >
                    <SelectTrigger data-testid="gingr-location-select">
                      <SelectValue placeholder="Select which Gingr location to use" />
                    </SelectTrigger>
                    <SelectContent>
                      {gingrLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id.toString()}>
                          {loc.name} (ID: {loc.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-700">
                    This maps your current app location to the selected Gingr location for accurate data filtering.
                  </p>
                </div>
              )}
              
              {/* Selected Location Preview (before save) */}
              {gingrConfig.gingr_location_id && gingrConfig.gingr_location_name && !savedGingrConfig?.gingr_location_name && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-900">Ready to Connect</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    {gingrConfig.gingr_location_name} (ID: {gingrConfig.gingr_location_id})
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Click "Save Configuration" to complete the connection.
                  </p>
                </div>
              )}
              
              {/* Error Display */}
              {gingrLocationsError && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-red-900">Error</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">{gingrLocationsError}</p>
                </div>
              )}
              
              <Button 
                onClick={saveGingrConfig} 
                data-testid="save-gingr-config-button"
                disabled={gingrLocations.length > 1 && !gingrConfig.gingr_location_id}
              >
                Save Configuration
              </Button>
              
              {gingrLocations.length > 1 && !gingrConfig.gingr_location_id && (
                <p className="text-xs text-amber-600">
                  ⚠️ Multiple locations detected. Please select a Gingr location before saving.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Knowledge Base for AI Insights */}
      <Card className="card border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <AlertCircle className="h-6 w-6 text-purple-600" />
            AI Knowledge Base
          </CardTitle>
          <CardDescription className="mt-1">
            Operating manuals available for AI Insights queries
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <h3 className="font-semibold text-gray-900 mb-3">📚 Available Documents</h3>
            {knowledgeBaseStats === null ? (
              <p className="text-sm text-gray-600">Loading knowledge base...</p>
            ) : knowledgeBaseStats && knowledgeBaseStats.documents && knowledgeBaseStats.documents.length > 0 ? (
              <div className="space-y-2">
                {knowledgeBaseStats.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded border border-purple-100">
                    <div>
                      <p className="font-medium text-purple-900">{doc._id}</p>
                      <p className="text-sm text-purple-700">{doc.chunk_count} searchable sections</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-purple-600" />
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <p className="text-sm text-purple-800">
                    ✨ <strong>Total:</strong> {knowledgeBaseStats.total_chunks} searchable sections
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Knowledge Base Not Populated</p>
                  <p className="text-xs text-amber-700">
                    The AI knowledge base is empty. Operating manuals need to be extracted and indexed.
                  </p>
                </div>
                {user && ["admin", "tenant_admin"].includes(user.role) && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800 font-medium mb-2">🔧 Setup Instructions (Admin Only):</p>
                    <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside ml-2">
                      <li>SSH into your production server</li>
                      <li>Navigate to: <code className="bg-blue-100 px-1 rounded">cd /app/backend</code></li>
                      <li>Run: <code className="bg-blue-100 px-1 rounded">python3 extract_knowledge_base.py</code></li>
                      <li>Wait for extraction to complete (~1-2 minutes)</li>
                      <li>Refresh this page to see the documents</li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="p-3 bg-purple-100 border border-purple-300 rounded-lg">
            <p className="text-sm text-purple-800">
              💡 <strong>How to use:</strong> Go to AI Insights and ask questions about procedures, policies, emergencies, or guest service. 
              The AI will automatically search the operating manuals and provide accurate answers.
            </p>
            <p className="text-xs text-purple-700 mt-2">
              Example: "How do I handle a dog fight?" or "What's the check-in procedure for boarding?"
            </p>
          </div>
        </CardContent>
      </Card>

      {/* AI Feedback Analytics - Admin Only */}
      {user && (["admin", "tenant_admin"].includes(user.role) || user.role === "super_admin" || user.is_super_admin) && (
        <Card className="card border-indigo-200 bg-indigo-50">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MessageSquare className="h-6 w-6 text-indigo-600" />
              AI Insights Feedback
            </CardTitle>
            <CardDescription className="mt-1">
              User feedback and satisfaction metrics for AI responses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiFeedbackStats ? (
              <>
                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white rounded-lg border border-indigo-200">
                    <div className="text-sm text-gray-600 mb-1">Total Feedback</div>
                    <div className="text-2xl font-bold text-indigo-700">{aiFeedbackStats.total_feedback}</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-sm text-gray-600 mb-1">👍 Helpful</div>
                    <div className="text-2xl font-bold text-green-700">{aiFeedbackStats.positive}</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="text-sm text-gray-600 mb-1">👎 Not Helpful</div>
                    <div className="text-2xl font-bold text-red-700">{aiFeedbackStats.negative}</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm text-gray-600 mb-1">Satisfaction Rate</div>
                    <div className="text-2xl font-bold text-blue-700">{aiFeedbackStats.satisfaction_rate}%</div>
                  </div>
                </div>

                {/* Recent Feedback with Comments */}
                {aiFeedbackStats.recent_feedback && aiFeedbackStats.recent_feedback.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Recent Feedback with Comments:</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {aiFeedbackStats.recent_feedback.map((feedback, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${feedback.rating === 'positive' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="text-xs text-gray-500">{feedback.user_email}</span>
                              <span className="text-xs text-gray-400 ml-2">{new Date(feedback.created_at).toLocaleDateString()}</span>
                            </div>
                            <span className="text-lg">{feedback.rating === 'positive' ? '👍' : '👎'}</span>
                          </div>
                          <div className="text-sm text-gray-700 mb-2">
                            <strong>Question:</strong> {feedback.user_message}
                          </div>
                          {feedback.comment && (
                            <div className="text-sm text-gray-600 italic bg-white p-2 rounded">
                              "{feedback.comment}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aiFeedbackStats.total_feedback === 0 && (
                  <div className="text-center p-8 bg-white rounded-lg border border-indigo-200">
                    <p className="text-gray-600">No feedback collected yet. Encourage your team to use the feedback buttons in AI Insights!</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center p-8">
                <p className="text-gray-600">Loading feedback statistics...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Management - Admin Only */}
      {/* Location Change Request (Non-Admin Users) */}
      {user && user.role !== "admin" && (
        <Card className="card">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MapPin className="h-6 w-6 text-blue-600" />
              Request Location Change
            </CardTitle>
            <CardDescription className="mt-1">
              Request to be assigned to a different location
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingRequest ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-900 mb-2">⏳ Pending Request</h3>
                <p className="text-sm text-yellow-700 mb-2">
                  You have requested to change to: <strong>{pendingRequest.requested_location_name}</strong>
                </p>
                <p className="text-xs text-yellow-600">
                  Requested on: {new Date(pendingRequest.requested_at).toLocaleString()}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Status: Waiting for admin approval
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="location-select">Select New Location</Label>
                <Select 
                  value={selectedLocationForChange} 
                  onValueChange={setSelectedLocationForChange}
                >
                  <SelectTrigger id="location-select">
                    <SelectValue placeholder="Choose a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations
                      .filter(loc => loc.id !== user?.location_id)
                      .map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={requestLocationChange}
                  className="mt-3 bg-gradient-to-r from-cyan-500 to-blue-600"
                >
                  Submit Request
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Location Management (Admin Only) */}
      {user && ["admin", "tenant_admin"].includes(user.role) && (
        <Card className="card border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Building2 className="h-6 w-6 text-green-600" />
                  Location Management
                </CardTitle>
                <CardDescription className="mt-1">
                  Create and manage locations for your organization
                </CardDescription>
              </div>
              <Dialog open={createLocationOpen} onOpenChange={setCreateLocationOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-green-500 to-emerald-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Location
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Location</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="location-name">Location Name *</Label>
                      <Input
                        id="location-name"
                        placeholder="K9 Resorts Garden City"
                        value={newLocation.name}
                        onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        placeholder="123 Main Street"
                        value={newLocation.address}
                        onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          placeholder="Garden City"
                          value={newLocation.city}
                          onChange={(e) => setNewLocation({...newLocation, city: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          placeholder="NY"
                          value={newLocation.state}
                          onChange={(e) => setNewLocation({...newLocation, state: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="zip">Zip Code</Label>
                        <Input
                          id="zip"
                          placeholder="11530"
                          value={newLocation.zip_code}
                          onChange={(e) => setNewLocation({...newLocation, zip_code: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          placeholder="267-915-2070"
                          value={newLocation.phone}
                          onChange={(e) => setNewLocation({...newLocation, phone: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateLocationOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={createLocation} className="bg-gradient-to-r from-green-500 to-emerald-600">
                      Create Location
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {locations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No locations yet. Create your first location to get started.</p>
                </div>
              ) : (
                locations.map((location) => (
                  <div key={location.id} className="p-4 bg-white rounded-lg border border-green-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">{location.name}</h3>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          {location.address && <p>📍 {location.address}</p>}
                          {(location.city || location.state) && (
                            <p>🏙️ {location.city}{location.city && location.state ? ', ' : ''}{location.state} {location.zip_code}</p>
                          )}
                          {location.phone && <p>📞 {location.phone}</p>}
                        </div>
                        {location.invite_code && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-500">Invite Code:</span>
                            <code className="px-2 py-1 bg-green-100 rounded text-xs font-mono text-green-700">
                              {location.invite_code}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
              <p className="text-sm text-green-800">
                📋 <strong>How to add users to a location:</strong>
                <br />1. Create the location here
                <br />2. Generate an invite code (see "Location Invite Codes" section below)
                <br />3. Share the invite code with new users
                <br />4. They register using the code and are automatically assigned to the location
                <br />5. Change their role to "admin" in Admin Users page if needed
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Code Management (Admin Only) */}
      {user && ["admin", "tenant_admin"].includes(user.role) && (
        <Card className="card border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Key className="h-6 w-6 text-blue-600" />
              Location Invite Codes
            </CardTitle>
            <CardDescription className="mt-1">
              Manage invite codes for user registration at each location
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {locations.map((location) => (
                <div key={location.id} className="p-4 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{location.name}</h3>
                      {location.invite_code ? (
                        <div className="flex items-center gap-2 mt-2">
                          <code className="px-3 py-1 bg-gray-100 rounded text-sm font-mono text-blue-600">
                            {location.invite_code}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(location.invite_code);
                              toast.success("Invite code copied!");
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">No invite code generated</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => generateInviteCode(location.id)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {location.invite_code ? "Regenerate" : "Generate"} Code
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 bg-blue-100 border border-blue-300 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Share these invite codes with new users during registration. 
                Each code assigns users to the corresponding location automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Database Migration Tool (Admin Only) */}
      {user && ["admin", "tenant_admin"].includes(user.role) && (
        <Card className="card border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <RefreshCw className="h-6 w-6 text-green-600" />
              Fix Lead Locations
            </CardTitle>
            <CardDescription className="mt-1">
              Assign Chalfont location to all leads that don't have a location assigned
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-white rounded-lg border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-2">🔧 Location Migration Tool</h3>
              <p className="text-sm text-gray-700 mb-3">
                If location admins can't see all leads, this tool will fix the issue by assigning 
                the Chalfont location to any leads that are missing a location_id.
              </p>
              <Button
                onClick={runLocationMigration}
                disabled={migrationRunning}
                className="bg-green-600 hover:bg-green-700"
              >
                {migrationRunning ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Running Migration...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Run Location Migration
                  </>
                )}
              </Button>
            </div>
            
            {migrationResult && (
              <div className={`p-4 rounded-lg border ${
                migrationResult.success 
                  ? 'bg-green-100 border-green-300' 
                  : 'bg-red-100 border-red-300'
              }`}>
                <div className="flex items-start gap-2">
                  {migrationResult.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-semibold ${migrationResult.success ? 'text-green-900' : 'text-red-900'}`}>
                      {migrationResult.success ? 'Success!' : 'Error'}
                    </p>
                    <p className={`text-sm ${migrationResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {migrationResult.message || migrationResult.error}
                    </p>
                    {migrationResult.success && migrationResult.leads_fixed > 0 && (
                      <div className="mt-2 text-sm text-green-700">
                        <p>• Fixed {migrationResult.leads_fixed} leads</p>
                        <p>• Total leads: {migrationResult.total_leads}</p>
                        <p>• Location: {migrationResult.location_name}</p>
                      </div>
                    )}
                    {migrationResult.success && migrationResult.leads_fixed === 0 && (
                      <p className="mt-2 text-sm text-green-700">
                        All leads already have proper location assignments. No changes needed.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {user && ["admin", "tenant_admin"].includes(user.role) && (
        <Card className="card border-red-200">
          <CardHeader>
            <CardTitle className="text-2xl text-red-700" style={{ fontFamily: 'Manrope' }}>
              Data Management (Admin Only)
            </CardTitle>
            <CardDescription className="mt-1">
              Dangerous operations - use with caution
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="font-semibold text-red-900 mb-2">⚠️ Cleanup Non-Gmail Leads</h3>
              <p className="text-sm text-red-700 mb-3">
                This will permanently delete all leads that were NOT created from Gmail sync. 
                Only leads with source="gmail_forward" will be kept.
              </p>
              <Button 
                variant="destructive"
                onClick={async () => {
                  if (window.confirm('Are you absolutely sure? This cannot be undone. All non-Gmail leads will be permanently deleted.')) {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.delete(`${API}/leads/cleanup`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      toast.success(response.data.message);
                      // Optionally refresh the page or lead count
                      window.location.reload();
                    } catch (error) {
                      console.error('Cleanup error:', error);
                      toast.error(error.response?.data?.detail || 'Failed to cleanup leads');
                    }
                  }
                }}
              >
                Delete Non-Gmail Leads
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Settings;
